package horcrux.common.api.dtos.types

import arrow.core.Either
import horcrux.common.HasBetterToString
import horcrux.common.types.JsonTransmittable
import io.ktor.utils.io.core.toByteArray
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic

@Serializable
sealed interface DocumentChunk : HasBetterToString

@Serializable
sealed interface SimpleDocumentChunk : DocumentChunk

@Serializable
sealed interface LocalDocumentChunk : DocumentChunk, JsonTransmittable {
    val chunkByteArray: ByteArray

    // TODO: Process image documents properly
    val asTextDocumentChunk: TextDocumentChunk
        get() = (this as? TextDocumentChunk) ?: TextDocumentChunk("<Image>")

    sealed interface DocumentChunkCompanion<TDocumentChunk : LocalDocumentChunk> :
        JsonTransmittable.Companion<TDocumentChunk> {
        override val jsonSerializer: Json
            get() = Json {
                this.serializersModule = SerializersModule {
                    polymorphic(LocalDocumentChunk::class) {
                        subclass(TextDocumentChunk::class, TextDocumentChunk.serializer())
                        subclass(ImageDocumentChunk::class, ImageDocumentChunk.serializer())
                        subclass(CompositeDocumentChunk::class, CompositeDocumentChunk.serializer())
                    }
                }
                this.classDiscriminator = "type"
            }
    }

    companion object : DocumentChunkCompanion<LocalDocumentChunk> {
        override fun deserializeFromByteArray(byteArray: ByteArray): Either<Throwable, LocalDocumentChunk> =
            deserializeFromBase64Json(serializer(), byteArray)
    }
}

@Serializable
data class TextDocumentChunk(override val value: String) : SimpleDocumentChunk, LocalDocumentChunk, TextDocument {
    override val chunkByteArray: ByteArray
        get() = value.toByteArray()
    override val jsonString: String
        get() = serializeToJsonString(DocumentChunk.serializer(), this)

    override fun toString(): String = "TextDocumentChunk<length=${value.length}>"

    companion object : LocalDocumentChunk.DocumentChunkCompanion<TextDocumentChunk> {
        override fun deserializeFromByteArray(byteArray: ByteArray): Either<Throwable, TextDocumentChunk> =
            deserializeFromBase64Json(serializer(), byteArray)
    }
}

@Serializable
data class ImageDocumentChunk(
    override val chunkByteArray: ByteArray
) : SimpleDocumentChunk, LocalDocumentChunk {
    override val jsonString: String
        get() = serializeToJsonString(DocumentChunk.serializer(), this)

    override fun toString(): String = "ImageDocumentChunk<size=${chunkByteArray.size}>"
    override fun betterToString(): String = toString()

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false

        other as ImageDocumentChunk

        return chunkByteArray.contentEquals(other.chunkByteArray)
    }

    override fun hashCode(): Int {
        return chunkByteArray.contentHashCode()
    }

    companion object : LocalDocumentChunk.DocumentChunkCompanion<ImageDocumentChunk> {
        override fun deserializeFromByteArray(byteArray: ByteArray): Either<Throwable, ImageDocumentChunk> =
            deserializeFromBase64Json(serializer(), byteArray)
    }
}

@Serializable
data class CompositeDocumentChunk(
    val textDocumentChunks: List<TextDocumentChunk>, val imageDocumentChunks: List<ImageDocumentChunk>
) : LocalDocumentChunk {
    constructor() : this(listOf(), listOf())

    override val chunkByteArray: ByteArray
        get() = super.byteArray

    override val jsonString: String
        get() = serializeToJsonString(LocalDocumentChunk.serializer(), this)

    override fun betterToString(): String = jsonString


    companion object : LocalDocumentChunk.DocumentChunkCompanion<CompositeDocumentChunk> {
        override fun deserializeFromByteArray(byteArray: ByteArray): Either<Throwable, CompositeDocumentChunk> =
            deserializeFromBase64Json(serializer(), byteArray)
    }
}

@Serializable
data class RemoteImageDocumentChunk(val documentURL: DocumentURL) : SimpleDocumentChunk {
    override fun betterToString(): String = documentURL.betterToString()
}
